/** sdoc API client used throughout the admin SPA. */

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || "http://127.0.0.1:8766";

const TOKEN_KEY = "sdoc_admin_token";
const USER_KEY = "sdoc_admin_user";

export interface SessionUser {
  role: string;
  tenant_id: string | null;
  email: string;
  /** The workspace belongs to a client demo: SmartRoots adds, converts and
   *  crawls; the server refuses those for this user, so the UI hides them. */
  demo_workspace?: boolean;
}

export function isDemoWorkspace(): boolean {
  return getUser()?.demo_workspace === true;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getUser(): SessionUser | null {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as SessionUser) : null;
}
export function setUser(u: SessionUser | null): void {
  if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_KEY);
}

/** Global auth-failure handler.
 *
 * Any 401 from an authed endpoint means the bearer is no longer valid (token
 * expired OR the user was deleted, e.g. after a dev DB reset). Clear the
 * cached session and bounce to /login instead of leaving the user on a page
 * full of raw "HTTP 401" toasts. The login redirect is a hard navigation so
 * React state is wiped along with localStorage.
 */
function handleAuthFailure(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
  // Avoid an infinite redirect loop if we're already on /login.
  if (!window.location.pathname.startsWith("/login")) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/login?next=${next}`);
  }
}

/** Pull a human-readable message out of a FastAPI error response. FastAPI's
 *  HTTPException surfaces the detail as `{"detail": "..."}` — showing that
 *  message directly is much friendlier than "HTTP 400: {...}". */
async function extractMessage(res: Response): Promise<string> {
  try {
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const j = await res.json();
      if (typeof j?.detail === "string") return j.detail;
      if (Array.isArray(j?.detail) && j.detail[0]?.msg) return String(j.detail[0].msg);
      return JSON.stringify(j);
    }
    return await res.text();
  } catch {
    return `Request failed (HTTP ${res.status})`;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { body } = await requestWithMeta<T>(path, init);
  return body;
}

/** Like ``request`` but also returns the response headers — used by paginated
 *  list endpoints that surface the total count via ``X-Total-Count``. */
async function requestWithMeta<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ body: T; headers: Headers }> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    handleAuthFailure();
    throw new Error("Session expired — redirecting to login.");
  }
  if (!res.ok) {
    // Every API error should read as a plain sentence — no HTTP codes, no
    // raw JSON. extractMessage() pulls FastAPI's `detail` field out.
    throw new Error(await extractMessage(res));
  }
  if (res.status === 204) return { body: undefined as T, headers: res.headers };
  const ct = res.headers.get("content-type") || "";
  const body = (ct.includes("application/json")
    ? await res.json()
    : await res.text()) as T;
  return { body, headers: res.headers };
}

export interface Page<T> {
  items: T[];
  total: number;
}

export interface LoginResponse {
  access_token: string;
  role: string;
  tenant_id: string | null;
  demo_workspace?: boolean;
}

/** One row from Conversion.stage_log — produced by sdoc_pipeline.types.StageResult. */
export interface StageLogEntry {
  name: string;
  status: "ok" | "degraded" | "failed" | "skipped";
  duration_ms: number;
  notes?: string | null;
  model_version?: string | null;
}

export interface Conversion {
  id: string;
  document_id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "degraded";
  forms_detected: number;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  // Track E — operational visibility + cost telemetry.
  stage_log?: StageLogEntry[] | null;
  model_override?: string | null;
  tokens_input?: number;
  tokens_output?: number;
  estimated_cost_usd?: number;
  // Real page count (PyMuPDF) — null until the conversion completes.
  page_count?: number | null;
  /** Part of a longer PDF: the original's pages converted ("1-50") and its page count. */
  page_range?: string | null;
  source_page_count?: number | null;
  // Document identity — surfaced by the API so the Workbench row can
  // identify the PDF without an extra round-trip.
  filename?: string | null;
  source_url?: string | null;
  site_domain?: string | null;
  sha256?: string | null;
  document_created_at?: string | null;
  // Quality gate — held for review instead of silently published.
  review_required?: boolean;
  review_reasons?: string[] | null;
  approved_at?: string | null;
  published?: boolean;
  /** Versions: each conversion of the same document is a version of it. */
  version?: number | null;
  version_count?: number | null;
  /** False when a newer version of the document exists (can't be approved). */
  is_latest?: boolean;
  /** The version visitors are served, if any (may be an older one). */
  live_conversion_id?: string | null;
  live_version?: number | null;
  /** Differences from the PDF still needing a decision (null = not listed yet). */
  open_blocking?: number | null;
  open_warnings?: number | null;
  /** Set on a version made by a reviewer's fix. */
  derived_from?: string | null;
  change_summary?: string | null;
}

// ────────── Review (fix after review) ──────────

export type ReviewItemKind =
  | "value" | "misread" | "fact" | "pdf_typo_fixed" | "missing" | "extra" | "picture_text" | "added_label"
  | "wording" | "dropped_sentence" | "invented_sentence" | "missing_picture";

export interface ReviewItem {
  id: string;
  page: number | null;
  kind: ReviewItemKind;
  severity: "blocking" | "warning";
  pdf_text: string;
  out_text: string;
  label: string;
  status: "open" | "fixed" | "accepted";
  note?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  resolution?: string | null;
}

export type PageState = "clean" | "flagged" | "warning" | "fixed" | "unchecked";

export interface ReviewPageSummary {
  page: number;
  state: PageState;
  blocking: number;
  warnings: number;
  resolved: number;
}

export interface ReviewData {
  conversion: Conversion;
  pages: ReviewPageSummary[];
  items: ReviewItem[];
  unchecked_pages: number[];
  items_available: boolean;
  open_blocking: number | null;
  open_warnings: number | null;
  can_edit: boolean;
  can_approve: boolean;
  edits_make_new_version: boolean;
  redo_model: string;
  max_redo_pages: number;
}

export interface ReviewPage {
  page: number;
  html: string;
  preview_html: string;
  styles: string;
  items: ReviewItem[];
}

export interface RedoEstimate {
  pages: number[];
  model: string;
  tokens_input: number;
  tokens_output: number;
  estimate_usd: number;
  max_usd: number;
}

export interface VersionChange {
  id: string;
  action: string;
  user: string | null;
  at: string | null;
  page: number | null;
  item_id: string | null;
  before: string | null;
  after: string | null;
  note: string | null;
}

export interface VersionHistoryEntry {
  conversion_id: string;
  version: number;
  status: Conversion["status"];
  created_at: string | null;
  derived_from_version: number | null;
  change_summary: string | null;
  published: boolean;
  live: boolean;
  approved_at: string | null;
  approved_by: string | null;
  estimated_cost_usd: number;
  error: string | null;
  changes: VersionChange[];
}

/** What a finished conversion means for the public site. */
export function reviewState(c: Conversion): "live" | "review" | null {
  if (c.status !== "succeeded" && c.status !== "degraded") return null;
  if (c.published) return "live";
  return c.review_required && !c.approved_at ? "review" : null;
}

export interface TenantThemeColors {
  primary?: string | null;
  primary_deep?: string | null;
  tint?: string | null;
  accent?: string | null;
}

export interface TenantTheme {
  brand_name?: string | null;
  brand_suffix?: string | null;
  brand_initials?: string | null;
  logo_url?: string | null;
  tagline?: string | null;
  colors?: TenantThemeColors | null;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  parent_tenant_id: string | null;
  page_quota: number | null;
  pages_used: number;
  is_suspended: boolean;
  qa_enabled: boolean;
  theme: TenantTheme | null;
  created_at: string;
}

/** Client demos (super-admin) — a private page of a prospect's own PDFs. */
export interface DemoItem {
  id: string;
  position: number;
  label: string;
  note: string | null;
  source_url: string | null;
  filename: string;
  size_bytes: number;
  page_count: number;
  is_scanned: boolean | null;
  is_tagged: boolean | null;
  has_form: boolean | null;
  document_id: string | null;
  created_at: string;
  /** The converted part, e.g. "1-50"; null = every page (or not converted). */
  page_range: string | null;
  conversion_id: string | null;
  conversion_status: Conversion["status"] | null;
  /** Shown on the demo page. */
  published: boolean;
  review_required: boolean;
  open_blocking: number | null;
  estimated_cost_usd: number;
  error: string | null;
}

export interface Demo {
  id: string;
  slug: string;
  url: string;
  client_name: string;
  client_website: string | null;
  title: string;
  intro: string | null;
  expires_at: string | null;
  enabled: boolean;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
  tenant: Tenant;
  items: DemoItem[];
  usd_spent: number;
  /** False once the workspace became a real customer. */
  owns_workspace: boolean;
  /** The client's real website in the workspace (crawl status), if added. */
  client_site: Site | null;
  discovered_count: number;
}

export interface MakeCustomerResult {
  demo: Demo;
  site_domain: string;
  /** Item labels: whole-PDF conversions moved to the real site / part-PDF ones left demo-only. */
  moved: string[];
  demo_only: string[];
}

export interface DemoEstimate {
  first_page: number;
  last_page: number;
  pages: number;
  page_count: number;
  page_range: string | null;
  usd_per_page: number;
  rate_based_on_pages: number;
  estimate_usd: number;
  max_usd: number;
  /** Room left in the demo's page limit (null = no limit). */
  pages_left: number | null;
}

export type DemoUpdate = Partial<{
  client_name: string;
  client_website: string | null;
  title: string;
  intro: string | null;
  expires_at: string | null;
  enabled: boolean;
  page_quota: number;
  qa_enabled: boolean;
}>;

export interface Invite {
  id: string;
  email: string;
  tenant_id: string;
  role: string;
  status: string;
  expires_at: string;
  accept_url: string;
  created_at: string;
  // Populated only on the create-invite response (not on the list endpoint).
  email_sent?: boolean | null;
  email_error?: string | null;
}

export interface Site {
  id: string;
  domain: string;
  homepage_url: string;
  status: "unverified" | "verified" | "crawling" | "ready" | "error";
  helper_script_installed: boolean;
  last_scanned_at: string | null;
  last_crawl_pdf_count: number;
  /** PDFs on record — matches the Discovered tabs and the plan's All PDFs. */
  pdf_count?: number | null;
  logo_url: string | null;
  qa_enabled: boolean | null;
  tenant_id: string;
  created_at: string;
  /** Only present when listSites was called with include_deleted=true.
   *  Truthy = soft-deleted (row should render greyed with a Restore action). */
  deleted_at?: string | null;
}

export interface DiscoveredLinkingPage {
  url: string;
  title: string | null;
  post_id?: number;
  /** "wp-plugin" | "crawl" | "visitor" (older rows may carry other values). */
  source?: string;
}

// ────────── Conversion plan ──────────

/** Computed priority group, in display order. */
export type Priority = "now" | "next" | "later" | "archive" | "duplicate" | "superseded" | "never" | "unscored";
/** Values an admin may set as a manual override (null clears it). */
export type PriorityOverride = "now" | "next" | "later" | "archive" | "never";
export const PRIORITY_ORDER: Priority[] = ["now", "next", "later", "archive", "duplicate", "superseded", "never", "unscored"];

/** Status of the newest conversion of the linked document (null = never converted). */
export type ConversionState = "converting" | "live" | "review" | "failed";

export type DocType =
  | "form" | "guide" | "schedule" | "map" | "policy" | "meeting"
  | "finance" | "notice" | "job" | "report" | "newsletter" | "other";
export const DOC_TYPES: DocType[] = [
  "form", "guide", "schedule", "map", "policy", "meeting",
  "finance", "notice", "job", "report", "newsletter", "other",
];

export interface PlanSettings {
  /** ISO date (YYYY-MM-DD). */
  compliance_date: string | null;
  archive_paths: string[];
  exclude_paths: string[];
  entity_size: "large" | "small";
  page_budget: number | null;
}

export interface PlanGroup {
  priority: Priority;
  count: number;
  pages: number;
  unknown_pages: number;
  est_cost_usd: number;
}

export interface PlanSummary {
  site_id: string;
  scored_at: string | null;
  groups: PlanGroup[];
  totals: { count: number; pages: number; est_cost_usd: number };
  /** Every PDF on record for the site, by status (same numbers as the Discovered tabs). */
  inventory?: { all: number; converted: number; queued: number; ignored: number; not_converted: number };
  settings: PlanSettings;
}

export interface Discovered {
  id: string;
  site_id: string;
  url: string;
  filename: string;
  content_length: number | null;
  last_modified: string | null;
  status: "discovered" | "selected" | "converted" | "ignored";
  document_id: string | null;
  discovered_at: string;
  // Real page count from PyMuPDF (captured at crawl). Falls back to
  // estimated_pages (byte-size heuristic) when null.
  page_count: number | null;
  // PR-1 streaming discovery state.
  source: "wp-plugin" | "wp-rest" | "sitemap" | "bfs" | "selenium" | "robots" | "tracker" | "crawl";
  found_on: string | null;
  scan_status: "pending" | "scanned" | "failed";
  scan_error: string | null;
  // PR-2 WP plugin extras.
  linking_pages?: DiscoveredLinkingPage[] | null;
  attachment_id?: number | null;
  external_title?: string | null;
  estimated_pages: number | null;
  estimated_cost_usd: number;
  // Conversion plan facts + priority. Optional so the page still renders
  // against an API that hasn't shipped them yet.
  content_sha?: string | null;
  has_form?: boolean | null;
  form_field_count?: number | null;
  is_scanned?: boolean | null;
  is_tagged?: boolean | null;
  pdf_title?: string | null;
  doc_type?: DocType | null;
  doc_type_source?: "rules" | "ai" | "override" | null;
  content_year?: number | null;
  is_private?: boolean;
  duplicate_of?: string | null;
  /** Newer version of this document (same file name, edited). */
  superseded_by?: string | null;
  /** Set when the file was replaced after it was converted. */
  content_changed_at?: string | null;
  priority?: Priority;
  priority_score?: number | null;
  priority_reasons?: string[] | null;
  priority_override?: PriorityOverride | null;
  page_views?: number;
  pdf_opens?: number;
  last_seen_at?: string | null;
  /** override ?? computed priority. */
  effective_priority?: Priority;
  conversion_id?: string | null;
  conversion_state?: ConversionState | null;
  review_reasons?: string[] | null;
}

export interface WpPluginManifest {
  version: string;
  download_url: string;
  sha256?: string | null;
  tested?: string | null;
}

export interface WpConnectorStatus {
  enabled: boolean;
  endpoint: string | null;
  token_set: boolean;
  last_sync_at: string | null;
  plugin_version: string | null;
  webhook_url?: string | null;
  last_webhook_at?: string | null;
  crawl_cadence_hours?: number;
}
export interface WpConnectorEnableResponse extends WpConnectorStatus {
  token: string;
}
export interface WpConnectorTestResponse {
  ok: boolean;
  version?: string | null;
  pdf_count?: number | null;
  error?: string | null;
}

export interface TrackerStatus {
  enabled: boolean;
  token_set: boolean;
  snippet: string | null;
  last_event_at: string | null;
}
export interface TrackerEnableResponse extends TrackerStatus {
  token: string;
}

export interface DiscoveredEstimate {
  count: number;
  estimated_pages: number;
  estimated_cost_usd: number;
  unknown_size_rows: number;
}

export type DiscoveredSortField =
  | "discovered_at" | "filename" | "content_length" | "page_count"
  | "last_modified" | "status" | "source" | "scan_status"
  // Conversion plan: priority order then score desc.
  | "priority_score" | "priority" | "content_year";

export interface DiscoveredFilters {
  q?: string;
  status_filter?: "discovered" | "selected" | "converted" | "ignored" | "all";
  site_id?: string;
  /** Effective priority, sent as a comma list. */
  priority?: Priority[];
  doc_type?: DocType;
  /** Sent as a comma list. */
  conversion_state?: ConversionState[];
  after?: string;
  before?: string;
  sort?: DiscoveredSortField;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export type ConversionSortField =
  | "created_at" | "started_at" | "completed_at" | "status"
  | "forms_detected" | "estimated_cost_usd" | "tokens_input" | "tokens_output";

export interface ConversionFilters {
  status_filter?: "all" | Conversion["status"] | "review" | "live";
  sort?: ConversionSortField;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  q?: string;
  /** One document's version history. */
  document_id?: string;
}

export const api = {
  base: API_BASE,
  async login(email: string, password: string): Promise<LoginResponse> {
    const res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error(await extractMessage(res));
    return res.json();
  },
  /** Request a password reset link. Always 204 — never leaks whether the
   *  email exists, so the caller shouldn't branch on the response. */
  async forgotPassword(email: string): Promise<void> {
    const res = await fetch(`${API_BASE}/v1/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) throw new Error(await extractMessage(res));
  },
  /** Consume a reset token + set a new password. Throws with the API's
   *  human-readable reason on 400/410 so the UI can render it verbatim. */
  async resetPassword(token: string, password: string): Promise<{ email: string }> {
    const res = await fetch(`${API_BASE}/v1/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    if (!res.ok) throw new Error(await extractMessage(res));
    return res.json();
  },

  // Tenant-scoped
  listConversions: async (filters: ConversionFilters = {}): Promise<Page<Conversion>> => {
    const params = new URLSearchParams();
    if (filters.status_filter) params.set("status_filter", filters.status_filter);
    if (filters.sort) params.set("sort", filters.sort);
    if (filters.order) params.set("order", filters.order);
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined) params.set("offset", String(filters.offset));
    if (filters.q) params.set("q", filters.q);
    if (filters.document_id) params.set("document_id", filters.document_id);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const { body, headers } = await requestWithMeta<Conversion[]>(`/v1/conversions${qs}`);
    return { items: body, total: Number(headers.get("X-Total-Count") ?? body.length) };
  },
  getConversion: (id: string): Promise<Conversion> => request(`/v1/conversions/${id}`),
  async uploadPdf(
    file: File,
    opts: { site_id?: string | null; source_url?: string | null } = {},
  ): Promise<Conversion> {
    const form = new FormData();
    form.append("pdf", file);
    if (opts.site_id) form.append("site_id", opts.site_id);
    if (opts.source_url) form.append("source_url", opts.source_url);
    const res = await fetch(`${API_BASE}/v1/conversions`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    if (res.status === 401) { handleAuthFailure(); throw new Error("Session expired — redirecting to login."); }
    if (!res.ok) throw new Error(await extractMessage(res));
    return res.json();
  },
  listSites: (opts: { includeDeleted?: boolean } = {}): Promise<Site[]> =>
    request(`/v1/sites${opts.includeDeleted ? "?include_deleted=true" : ""}`),
  restoreSite: (id: string): Promise<Site> =>
    request(`/v1/sites/${id}/restore`, { method: "POST" }),
  addSite: (homepage_url: string): Promise<Site> =>
    request("/v1/sites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ homepage_url }),
    }),
  checkSnippet: (id: string): Promise<Site> =>
    request(`/v1/sites/${id}/check-snippet`, { method: "POST" }),
  crawlSite: (id: string): Promise<Site> =>
    request(`/v1/sites/${id}/crawl`, { method: "POST" }),
  getSnippet: (): Promise<{ snippet: string; tenant_slug: string }> => request("/v1/sites/snippet"),

  // Discovered PDFs
  listDiscovered: async (filters: DiscoveredFilters = {}): Promise<Page<Discovered>> => {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.status_filter) params.set("status_filter", filters.status_filter);
    if (filters.site_id) params.set("site_id", filters.site_id);
    if (filters.priority?.length) params.set("priority", filters.priority.join(","));
    if (filters.doc_type) params.set("doc_type", filters.doc_type);
    if (filters.conversion_state?.length) params.set("conversion_state", filters.conversion_state.join(","));
    if (filters.after) params.set("after", filters.after);
    if (filters.before) params.set("before", filters.before);
    if (filters.sort) params.set("sort", filters.sort);
    if (filters.order) params.set("order", filters.order);
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined) params.set("offset", String(filters.offset));
    const qs = params.toString() ? `?${params.toString()}` : "";
    const { body, headers } = await requestWithMeta<Discovered[]>(`/v1/discovered${qs}`);
    return { items: body, total: Number(headers.get("X-Total-Count") ?? body.length) };
  },
  discoveredCounts: (): Promise<Record<string, number>> => request("/v1/discovered/counts"),
  convertDiscovered: (ids: string[]): Promise<Discovered[]> =>
    request("/v1/discovered/convert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  estimateDiscovered: (ids: string[]): Promise<DiscoveredEstimate> =>
    request("/v1/discovered/estimate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  ignoreDiscovered: (ids: string[]): Promise<Discovered[]> =>
    request("/v1/discovered/ignore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  // ────────── Conversion plan ──────────
  getPlan: (siteId: string): Promise<PlanSummary> =>
    request(`/v1/discovered/plan?site_id=${encodeURIComponent(siteId)}`),
  setPriority: (ids: string[], priority: PriorityOverride | null): Promise<Discovered[]> =>
    request("/v1/discovered/priority", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, priority }),
    }),
  rescoreSite: (siteId: string): Promise<{ queued: number }> =>
    request("/v1/discovered/rescore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site_id: siteId }),
    }),
  refreshFacts: (siteId: string): Promise<{ queued: number }> =>
    request("/v1/discovered/refresh-facts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ site_id: siteId }),
    }),
  getPlanSettings: (siteId: string): Promise<PlanSettings> =>
    request(`/v1/sites/${siteId}/plan-settings`),
  putPlanSettings: (siteId: string, settings: PlanSettings): Promise<PlanSettings> =>
    request(`/v1/sites/${siteId}/plan-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    }),
  /** Download the plan as CSV. Authed fetch → blob → temporary <a download>
   *  (a plain link can't carry the bearer token). */
  async exportPlanCsv(siteId: string, priority?: Priority[], filenameHint?: string): Promise<void> {
    const params = new URLSearchParams({ site_id: siteId });
    if (priority?.length) params.set("priority", priority.join(","));
    const res = await fetch(`${API_BASE}/v1/discovered/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (res.status === 401) { handleAuthFailure(); throw new Error("Session expired — redirecting to login."); }
    if (!res.ok) throw new Error(await extractMessage(res));
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const name = m ? decodeURIComponent(m[1]) : `${filenameHint || "conversion-plan"}.csv`;
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  },

  // ────────── WP connector (PR-2) ──────────
  wpConnectorStatus: (siteId: string): Promise<WpConnectorStatus> =>
    request(`/v1/sites/${siteId}/wp-connector`),
  wpConnectorEnable: (siteId: string, endpoint?: string): Promise<WpConnectorEnableResponse> =>
    request(`/v1/sites/${siteId}/wp-connector/enable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: endpoint || null }),
    }),
  wpConnectorRotate: (siteId: string): Promise<WpConnectorEnableResponse> =>
    request(`/v1/sites/${siteId}/wp-connector/rotate`, { method: "POST" }),
  wpConnectorDisable: (siteId: string): Promise<void> =>
    request(`/v1/sites/${siteId}/wp-connector/disable`, { method: "POST" }),
  wpConnectorTest: (siteId: string): Promise<WpConnectorTestResponse> =>
    request(`/v1/sites/${siteId}/wp-connector/test`, { method: "POST" }),
  wpConnectorSync: (siteId: string): Promise<{ task_id: string }> =>
    request(`/v1/sites/${siteId}/wp-connector/sync`, { method: "POST" }),
  updateCrawlCadence: (siteId: string, hours: number): Promise<WpConnectorStatus> =>
    request(`/v1/sites/${siteId}/crawl-cadence`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ crawl_cadence_hours: hours }),
    }),
  /** URL of the plugin manifest — points at the JSON metadata endpoint. */
  wpPluginManifestUrl: (): string => `${API_BASE}/wp-plugin/manifest.json`,
  /** Direct URL to the plugin zip for a given version (served by the API). */
  wpPluginZipUrl: (version: string): string =>
    `${API_BASE}/wp-plugin/download/${encodeURIComponent(version)}.zip`,
  /** Fetch the plugin manifest (version, download_url, sha256). */
  wpPluginManifest: (): Promise<WpPluginManifest> =>
    request(`/wp-plugin/manifest.json`),

  // ────────── Tracker (PR-3) ──────────
  trackerStatus: (siteId: string): Promise<TrackerStatus> =>
    request(`/v1/sites/${siteId}/tracker`),
  trackerEnable: (siteId: string): Promise<TrackerEnableResponse> =>
    request(`/v1/sites/${siteId}/tracker/enable`, { method: "POST" }),
  trackerRotate: (siteId: string): Promise<TrackerEnableResponse> =>
    request(`/v1/sites/${siteId}/tracker/rotate`, { method: "POST" }),
  trackerDisable: (siteId: string): Promise<void> =>
    request(`/v1/sites/${siteId}/tracker/disable`, { method: "POST" }),

  deleteDiscovered: (id: string): Promise<void> =>
    request(`/v1/discovered/${id}`, { method: "DELETE" }),

  // Site CRUD
  updateSite: (id: string, homepage_url: string): Promise<Site> =>
    request(`/v1/sites/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ homepage_url }),
    }),
  deleteSite: (id: string): Promise<void> =>
    request(`/v1/sites/${id}`, { method: "DELETE" }),
  async uploadSiteLogo(id: string, file: File): Promise<Site> {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/v1/sites/${id}/logo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    if (res.status === 401) { handleAuthFailure(); throw new Error("Session expired — redirecting to login."); }
    if (!res.ok) throw new Error(await extractMessage(res));
    return res.json();
  },
  deleteSiteLogo: (id: string): Promise<Site> =>
    request(`/v1/sites/${id}/logo`, { method: "DELETE" }),

  // Conversion delete
  deleteConversion: (id: string): Promise<void> =>
    request(`/v1/conversions/${id}`, { method: "DELETE" }),
  approveConversion: (id: string): Promise<Conversion> =>
    request(`/v1/conversions/${id}/approve`, { method: "POST" }),

  // Review screen — differences from the PDF and the fixes for them.
  getReview: (id: string): Promise<ReviewData> => request(`/v1/conversions/${id}/review`),
  getReviewPage: (id: string, page: number): Promise<ReviewPage> =>
    request(`/v1/conversions/${id}/pages/${page}`),
  /** The original PDF page as an object URL (authed fetch → blob). Revoke when done. */
  async pageImageUrl(id: string, page: number, width = 1000): Promise<string> {
    const token = getToken();
    const res = await fetch(`${API_BASE}/v1/conversions/${id}/pages/${page}/image?width=${width}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) { handleAuthFailure(); throw new Error("Session expired — redirecting to login."); }
    if (!res.ok) throw new Error(await extractMessage(res));
    return URL.createObjectURL(await res.blob());
  },
  resolveItem: (
    id: string, itemId: string, action: "use_pdf" | "edit" | "accept", opts: { text?: string; note?: string } = {},
  ): Promise<ReviewData> =>
    request(`/v1/conversions/${id}/items/${encodeURIComponent(itemId)}/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, text: opts.text ?? null, note: opts.note ?? null }),
    }),
  editPage: (id: string, page: number, html: string, note?: string): Promise<ReviewData> =>
    request(`/v1/conversions/${id}/pages/${page}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ html, note: note ?? null }),
    }),
  estimateRedo: (id: string, pages: number[]): Promise<RedoEstimate> =>
    request(`/v1/conversions/${id}/redo/estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pages }),
    }),
  redoPages: (id: string, pages: number[], acceptedEstimateUsd: number): Promise<Conversion> =>
    request(`/v1/conversions/${id}/redo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pages, accepted_estimate_usd: acceptedEstimateUsd }),
    }),
  getHistory: (id: string): Promise<VersionHistoryEntry[]> => request(`/v1/conversions/${id}/history`),

  unpublishConversion: (id: string): Promise<Conversion> =>
    request(`/v1/conversions/${id}/unpublish`, { method: "POST" }),

  reconvertConversion: (id: string, model_override?: string | null): Promise<Conversion> =>
    request(`/v1/conversions/${id}/reconvert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model_override: model_override ?? null }),
    }),

  // Super-admin
  renameTenant: (id: string, name: string, slug: string, page_quota: number | null): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, slug, page_quota }),
    }),
  deleteTenant: (id: string): Promise<void> =>
    request(`/v1/superadmin/tenants/${id}`, { method: "DELETE" }),
  unsuspendTenant: (id: string): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}/unsuspend`, { method: "POST" }),
  listInvites: (): Promise<Invite[]> => request("/v1/invites"),
  revokeInvite: (id: string): Promise<void> =>
    request(`/v1/invites/${id}/revoke`, { method: "POST" }),
  deleteInvite: (id: string): Promise<void> =>
    request(`/v1/invites/${id}`, { method: "DELETE" }),

  // Super-admin
  listTenants: (): Promise<Tenant[]> => request("/v1/superadmin/tenants"),
  createTenant: (name: string, slug: string, page_quota: number | null): Promise<Tenant> =>
    request("/v1/superadmin/tenants", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, slug, page_quota }),
    }),
  updateQuota: (id: string, page_quota: number | null): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}/quota`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page_quota }),
    }),
  suspendTenant: (id: string): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}/suspend`, { method: "POST" }),
  createInvite: (email: string, tenant_id: string, role = "owner"): Promise<Invite> =>
    request("/v1/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, tenant_id, role }),
    }),

  // Super-admin: Ask-a-Question toggle (per tenant + per site)
  setTenantQa: (id: string, enabled: boolean): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}/qa`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    }),
  setTenantTheme: (id: string, theme: TenantTheme): Promise<Tenant> =>
    request(`/v1/superadmin/tenants/${id}/theme`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(theme),
    }),
  listAllSites: (): Promise<Site[]> => request("/v1/superadmin/sites"),
  setSiteQa: (id: string, enabled: boolean | null): Promise<Site> =>
    request(`/v1/superadmin/sites/${id}/qa`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    }),

  // Super-admin: client demos
  listDemos: (): Promise<Demo[]> => request("/v1/superadmin/demos"),
  getDemo: (id: string): Promise<Demo> => request(`/v1/superadmin/demos/${id}`),
  createDemo: (body: {
    client_name: string; client_website?: string | null; title?: string | null;
    intro?: string | null; expires_at?: string | null; page_quota?: number;
  }): Promise<Demo> =>
    request("/v1/superadmin/demos", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  updateDemo: (id: string, body: DemoUpdate): Promise<Demo> =>
    request(`/v1/superadmin/demos/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  deleteDemo: (id: string): Promise<void> =>
    request(`/v1/superadmin/demos/${id}`, { method: "DELETE" }),
  /** Adds a PDF (file, or URL the server downloads). Converts nothing. */
  async addDemoItem(
    demoId: string,
    opts: { label: string; note?: string | null; url?: string | null; file?: File | null },
  ): Promise<DemoItem> {
    const form = new FormData();
    form.append("label", opts.label);
    if (opts.note) form.append("note", opts.note);
    if (opts.url) form.append("url", opts.url);
    if (opts.file) form.append("pdf", opts.file);
    const res = await fetch(`${API_BASE}/v1/superadmin/demos/${demoId}/items`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    if (res.status === 401) { handleAuthFailure(); throw new Error("Session expired — redirecting to login."); }
    if (!res.ok) throw new Error(await extractMessage(res));
    return res.json();
  },
  updateDemoItem: (
    demoId: string, itemId: string,
    body: Partial<{ label: string; note: string | null; source_url: string | null; position: number }>,
  ): Promise<DemoItem> =>
    request(`/v1/superadmin/demos/${demoId}/items/${itemId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  /** One crawl of the client's website — converts nothing, never repeats on its own. */
  crawlDemoSite: (demoId: string): Promise<Demo> =>
    request(`/v1/superadmin/demos/${demoId}/crawl`, { method: "POST" }),
  makeCustomer: (demoId: string, body: { name: string; website: string; page_quota: number | null; keep_page: boolean }): Promise<MakeCustomerResult> =>
    request(`/v1/superadmin/demos/${demoId}/make-customer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  removeDemoItem: (demoId: string, itemId: string): Promise<void> =>
    request(`/v1/superadmin/demos/${demoId}/items/${itemId}`, { method: "DELETE" }),
  estimateDemoItem: (demoId: string, itemId: string, pages: string | null): Promise<DemoEstimate> =>
    request(`/v1/superadmin/demos/${demoId}/items/${itemId}/estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pages }),
    }),
  convertDemoItem: (demoId: string, itemId: string, pages: string | null, acceptedEstimateUsd: number): Promise<DemoItem> =>
    request(`/v1/superadmin/demos/${demoId}/items/${itemId}/convert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pages, accepted_estimate_usd: acceptedEstimateUsd }),
    }),

  // Public (invite accept)
  peekInvite: (token: string): Promise<{ email: string; tenant_name: string; role: string; expires_at: string }> =>
    request(`/v1/invites/by-token/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string): Promise<{ user_id: string; email: string }> =>
    request("/v1/invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    }),
};
